import { useEffect, useRef } from 'react';
import { useToast } from '../composants/Toast';
import type { AutoSaveStatus } from './useAutoSave';

export interface AutoSaveSource {
  status: AutoSaveStatus;
  error: string | null;
}

export interface AutoSaveToastsOptions {
  /**
   * Erreur de validation client bloquante (ex : « SIRET invalide »). Les saveFn
   * d'autosave font un `return` silencieux quand la saisie est invalide : l'autosave
   * croit alors avoir enregistre (statut « saved ») alors que rien n'est parti.
   * Quand cette valeur est renseignee, on affiche l'erreur au lieu d'un faux succes.
   */
  blockingError?: string | null;
}

type Phase = 'idle' | 'saving' | 'saved' | 'error';

// Le toast « Enregistrement… » est unique (un seul enregistrement a la fois) : id fixe.
const SAVING_ID = 'profil-autosave-saving';
// Le toast d'erreur est unique (evite le spam a chaque frappe) : id fixe, mis a jour en place.
const ERROR_ID = 'profil-autosave-error';
// Chaque succes recoit un id unique -> les confirmations S'EMPILENT (une nouvelle par-dessus).
const SAVED_PREFIX = 'profil-autosave-saved-';
let savedCounter = 0;

/**
 * Reflete l'etat de l'autosave dans des toasts empilables (bas a droite).
 *
 * Comportement voulu (empilement, pas de remplacement en place) :
 *  - saving -> toast « Enregistrement… » persistant, reste TANT QUE ce n'est pas enregistre
 *  - saved  -> le « Enregistrement… » disparait et une confirmation « Enregistre » s'empile ;
 *              un nouvel enregistrement empile une NOUVELLE confirmation par-dessus
 *  - error  -> le « Enregistrement… » disparait, un toast d'erreur s'affiche (erreur serveur
 *              OU validation bloquante), et reste plus longtemps
 *
 * Plusieurs autosaves d'un meme onglet sont agreges par priorite blockingError > saving > error > saved.
 * Un seul onglet profil est actif a la fois : pas de conflit entre onglets.
 */
export function useAutoSaveToasts(sources: AutoSaveSource[], options?: AutoSaveToastsOptions): void {
  const toast = useToast();
  const blockingError = options?.blockingError ?? null;

  const anySaving = sources.some((s) => s.status === 'saving');
  const firstError = sources.find((s) => s.status === 'error');
  const anySaved = sources.some((s) => s.status === 'saved');

  let phase: Phase = 'idle';
  if (blockingError) phase = 'error';
  else if (anySaving) phase = 'saving';
  else if (firstError) phase = 'error';
  else if (anySaved) phase = 'saved';

  const errorText = blockingError || firstError?.error || null;

  // Etat precedent (phase + cle) pour ne reagir qu'aux transitions.
  const prevRef = useRef<{ phase: Phase; key: string }>({ phase: 'idle', key: 'idle' });

  useEffect(() => {
    // La cle inclut le message d'erreur : une erreur qui change re-emet le toast.
    const key = phase === 'error' ? `error:${errorText ?? ''}` : phase;
    if (key === prevRef.current.key) return;
    const prevPhase = prevRef.current.phase;
    prevRef.current = { phase, key };

    // En quittant une phase transitoire, on retire son toast dedie.
    if (prevPhase === 'saving' && phase !== 'saving') toast.dismiss(SAVING_ID);
    if (prevPhase === 'error' && phase !== 'error') toast.dismiss(ERROR_ID);

    if (phase === 'saving') {
      toast.show({
        id: SAVING_ID,
        type: 'info',
        position: 'bottom-right',
        loading: true,
        persistent: true,
        dismissible: false,
        message: 'Enregistrement…',
      });
    } else if (phase === 'saved') {
      savedCounter += 1;
      toast.show({
        id: `${SAVED_PREFIX}${savedCounter}`,
        type: 'success',
        position: 'bottom-right',
        title: 'Enregistré',
        message: 'Vos modifications ont bien été enregistrées.',
        duration: 4500,
      });
    } else if (phase === 'error') {
      toast.show({
        id: ERROR_ID,
        type: 'error',
        position: 'bottom-right',
        title: 'Modifications non enregistrées',
        message: errorText || "Une erreur est survenue, vos modifications n'ont pas été enregistrées.",
        duration: 8000,
      });
    }
    // phase 'idle' : rien a faire, les confirmations expirent seules.
  }, [phase, errorText, toast]);

  // Quitter l'onglet pendant un enregistrement : on retire le toast persistant « Enregistrement… ».
  useEffect(() => () => {
    toast.dismiss(SAVING_ID);
  }, [toast]);
}

export default useAutoSaveToasts;
